import { Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ViewIcon from "@mui/icons-material/RemoveRedEye";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Switch, FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import axios from "axios";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import OfferDetailsView from "./OfferDetailsView"; // the view component from before

import {
  addOffer,
  deleteOffer,
  getOffers,
  updateOffer,
} from "../../../../store/actions/offersActions";

const OfferManagement = ({ provider }) => {
  const dispatch = useDispatch();
  const offerState = useSelector((state) => state.offers || {});
  const offersFromStore = offerState.list || [];
  const [all_categories, setAllCategories] = useState([]);
  const [all_vouchers, setAllVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [offerForm, setOfferForm] = useState({
    type: "DAY",
    title: "",
    short_desc: "",
    long_desc: "",
    terms: "",
    images: [],
    provider: provider?._id || "",
    categories: [],
    sponsored_label: "",
    price: { before: 0, after: 0, currency: "EUR" },
    voucherId: "",
    valid_from: new Date(),
    valid_to: new Date(),
    inventory: { max_redemptions: 0, remaining: 0, per_user_per_day: 0 },
    priority: 1,
    sponsored: false,
    active: true,
  });
  const [openView, setOpenView] = useState(false);

  const handleView = (offer) => {
    setOpenView(offer);
  };

  const handleClose = () => {
    setOpenView(false);
  };
  const offersPerPage = 5;

  useEffect(() => {
    if (provider?._id) {
      setLoading(true);
      dispatch(getOffers(provider._id));
      const requestOptions = {
        method: "GET",
        redirect: "follow",
      };

      fetch(
        `${process.env.REACT_APP_API_URL}/offers/getVouchersAndCategories/${provider?._id}`,
        requestOptions
      )
        .then((response) => response.json())
        .then((result) => {
          setAllCategories(result.categories);
          setAllVouchers(result.vouchers);
        })
        .catch((error) => {
          console.log("Error");
        });
    }
  }, [provider, dispatch]);

  useEffect(() => {
    setLoading(false);
  }, [offersFromStore]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const filteredOffers = offersFromStore.filter((o) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (o.title || "").toLowerCase().includes(q) ||
      (o.short_desc || "").toLowerCase().includes(q) ||
      (o.long_desc || "").toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(filteredOffers.length / offersPerPage) || 1;
  const currentOffers = filteredOffers.slice(
    (page - 1) * offersPerPage,
    page * offersPerPage
  );

  const openAddDialog = () => {
    setEditingOffer(null);
    setOfferForm({
      type: "DAY",
      title: "",
      short_desc: "",
      long_desc: "",
      terms: "",
      images: [],
      provider: provider?._id || "",
      categories: [],
      price: { before: 0, after: 0, currency: "EUR" },
      valid_from: new Date(),
      valid_to: new Date(),
      inventory: { max_redemptions: 0, remaining: 0, per_user_per_day: 0 },
      priority: 1,
      sponsored: false,
      active: true,
    });
    setOpenDialog(true);
  };

  const openEditDialog = (offer) => {
    setEditingOffer({
      ...offer,
      categories: offer.categories.map((item) => item._id),
    });
    setOfferForm({
      ...offer,
      categories: offer.categories.map((item) => item._id),
    });
    setOpenDialog(true);
  };

  const handleSaveOffer = async () => {
    let uploadedImages = offerForm.images;

    // if a new file is chosen
    if (imageFile) {
      const imageFormData = new FormData();
      imageFormData.append("file", imageFile);

      const uploadRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/uploadFile`,
        imageFormData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      uploadedImages = [{ url: uploadRes.data.url, alt: imageFile.name }];
    }

    const payload = {
      ...offerForm,
      provider: provider?._id,
      categories: offerForm.categories,
      images: uploadedImages,
    };

    if (editingOffer?._id) {
      await dispatch(updateOffer({ id: editingOffer._id, payload }));
    } else {
      await dispatch(addOffer(payload));
    }

    dispatch(getOffers(provider._id));
    setOpenDialog(false);
    setEditingOffer(null);
    setImageFile(null);
  };

  const handleDelete = (offer) => {
    if (!window.confirm(`Delete this offer? ${offer.title}`)) return;
    dispatch(deleteOffer({ offerId: offer._id, providerId: provider._id }));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ maxWidth: "99%", mx: "auto" }}>
        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <TextField
            size="small"
            placeholder="Search offers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} />,
            }}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAddDialog}
          >
            Add Offer
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : currentOffers.length === 0 ? (
          <Typography>No offers found</Typography>
        ) : (
          <Stack spacing={2}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Image</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Short Desc</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Valid From</TableCell>
                    <TableCell>Valid To</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentOffers.map((o) => (
                    <TableRow key={o._id}>
                      <TableCell>
                        <Avatar
                          src={o.images?.[0]?.url}
                          alt={o.images?.[0]?.alt}
                          sx={{ width: 32, height: 32 }}
                        />
                      </TableCell>
                      <TableCell>{o.title}</TableCell>
                      <TableCell>{o.short_desc}</TableCell>
                       <TableCell>{o.type == "WEEK" ? "Weekly":"Daily"}</TableCell>
                      <TableCell>
                        {o.price?.after} {o.price?.currency}
                      </TableCell>
                      <TableCell>
                        {o.active ? "✅ Active" : "❌ Inactive"}
                      </TableCell>
                      <TableCell>
                        {o.valid_from
                          ? moment(o.valid_from).format("DD-MM-YYYY HH:MM")
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {o.valid_to
                          ? moment(o.valid_to).format("DD-MM-YYYY HH:MM")
                          : "-"}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() => handleView(o)}
                          color="info"
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => openEditDialog(o)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(o)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}

        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, v) => setPage(v)}
            />
          </Box>
        )}

        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{editingOffer ? "Edit Offer" : "Add Offer"}</DialogTitle>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
          >
            <TextField
              label="Title"
              value={offerForm.title}
              onChange={(e) =>
                setOfferForm({ ...offerForm, title: e.target.value })
              }
            />
            <TextField
              label="Short Description"
              value={offerForm.short_desc}
              onChange={(e) =>
                setOfferForm({ ...offerForm, short_desc: e.target.value })
              }
            />
            <TextField
              label="Long Description"
              value={offerForm.long_desc}
              onChange={(e) =>
                setOfferForm({ ...offerForm, long_desc: e.target.value })
              }
              multiline
              rows={3}
            />
            <TextField
              label="Terms"
              value={offerForm.terms}
              onChange={(e) =>
                setOfferForm({ ...offerForm, terms: e.target.value })
              }
            />

            <TextField
              label="Sponsored Label"
              value={offerForm.sponsored_label}
              onChange={(e) =>
                setOfferForm({ ...offerForm, sponsored_label: e.target.value })
              }
            />



            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">
                Select Type
              </InputLabel>

              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
               
                label="Select Type"
                value={offerForm.type} // must be an array
                onChange={(e) => {
                  setOfferForm({
                    ...offerForm,
                    type: e.target.value, // MUI gives you the full array
                  });
                }}
              >
                {[{label:"Daily", value:"DAY"},{label:"Weekly", value:"WEEK"} ].map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>


            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">
                Select Categories
              </InputLabel>

              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                multiple={true}
                label="Select Categories"
                value={offerForm.categories} // must be an array
                onChange={(e) => {
                  setOfferForm({
                    ...offerForm,
                    categories: e.target.value, // MUI gives you the full array
                  });
                }}
              >
                {all_categories.map((item) => (
                  <MenuItem key={item._id} value={item._id}>
                    {item.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>



            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label2">Select Voucher</InputLabel>
              <Select
                labelId="demo-simple-select-label2"
                id="demo-simple-select2"
                label="Select Voucher"
                value={offerForm.voucherId} // must be an array
                onChange={(e) => {
                  setOfferForm({
                    ...offerForm,
                    voucherId: e.target.value, // MUI gives you the full array
                  });
                }}
              >
                {all_vouchers.map((item) => (
                  <MenuItem key={item._id} value={item._id}>
                    {item.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Price */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Price Before"
                type="number"
                value={offerForm.price.before}
                onChange={(e) =>
                  setOfferForm({
                    ...offerForm,
                    price: { ...offerForm.price, before: e.target.value },
                  })
                }
              />
              <TextField
                label="Price After"
                type="number"
                value={offerForm.price.after}
                onChange={(e) =>
                  setOfferForm({
                    ...offerForm,
                    price: { ...offerForm.price, after: e.target.value },
                  })
                }
              />
              <TextField
                label="Currency"
                value={offerForm.price.currency}
                onChange={(e) =>
                  setOfferForm({
                    ...offerForm,
                    price: { ...offerForm.price, currency: e.target.value },
                  })
                }
              />
            </Box>

            {/* Inventory */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Max Redemptions"
                type="number"
                value={offerForm.inventory.max_redemptions}
                onChange={(e) =>
                  setOfferForm({
                    ...offerForm,
                    inventory: {
                      ...offerForm.inventory,
                      max_redemptions: e.target.value,
                    },
                  })
                }
              />
              <TextField
                label="Remaining"
                type="number"
                value={offerForm.inventory.remaining}
                onChange={(e) =>
                  setOfferForm({
                    ...offerForm,
                    inventory: {
                      ...offerForm.inventory,
                      remaining: e.target.value,
                    },
                  })
                }
              />
              <TextField
                label="Per User Per Day"
                type="number"
                value={offerForm.inventory.per_user_per_day}
                onChange={(e) =>
                  setOfferForm({
                    ...offerForm,
                    inventory: {
                      ...offerForm.inventory,
                      per_user_per_day: e.target.value,
                    },
                  })
                }
              />
            </Box>

            {/* Dates */}
            <DateTimePicker
              label="Valid From"
              value={
                offerForm.valid_from ? new Date(offerForm.valid_from) : null
              }
              onChange={(v) => setOfferForm({ ...offerForm, valid_from: v })}
            />

            <DateTimePicker
              label="Valid To"
              value={offerForm.valid_to ? new Date(offerForm.valid_to) : null}
              onChange={(v) => setOfferForm({ ...offerForm, valid_to: v })}
            />
            {/* Upload */}
            <Button variant="outlined" component="label">
              Upload Image
              <input
                type="file"
                hidden
                onChange={(e) => setImageFile(e.target.files[0])}
              />
            </Button>

            <FormControlLabel
              control={
                <Switch
                  checked={offerForm.active}
                  onChange={(e) =>
                    setOfferForm({ ...offerForm, active: e.target.checked })
                  }
                />
              }
              label="Active"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={offerForm.sponsored}
                  onChange={(e) =>
                    setOfferForm({ ...offerForm, sponsored: e.target.checked })
                  }
                />
              }
              label="Sponsored"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveOffer}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(openView)}
          onClose={handleClose}
          fullWidth
          maxWidth="md"
          scroll="paper"
        >
          <DialogTitle>
            <Typography variant="h6" fontWeight="bold">
              Offer Details
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            <OfferDetailsView data={openView} />
          </DialogContent>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default OfferManagement;
